import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useConfig } from "@/lib/auth";
import { useSite, siteImg, siteText, isPreview } from "@/lib/site";
import { VehicleGrid } from "@/components/xd/VehicleCard";

const pos = (p, d) => `${(p || d)[0]}% ${(p || d)[1]}%`;

export const HeroView = ({ site }) => {
  const { t, lang } = useLang();
  const { content } = useConfig();
  const h = site?.hero || {};
  const tx = (k, fb) => siteText(site, k, lang, fb);
  const links = site?.links || { hero_cta: "/inventory", hero_cta2: "/sold" };
  return (
    <section className="relative overflow-hidden grain flex flex-col md:block md:h-[calc(100vh-5rem)] md:min-h-[560px]" data-testid="home-hero"
      style={{ "--pos-m": pos(h.pos_mobile, [50, 100]), "--pos-d": pos(h.pos_desktop, [50, 62]) }}>
      <div className="relative h-[48svh] min-h-[300px] max-h-[560px] md:absolute md:inset-0 md:h-auto md:max-h-none" data-testid="hero-image-frame">
        <img src={h.image_id ? siteImg(h.image_id) : "/hero.jpg"} alt="" className="absolute inset-0 h-full w-full object-cover object-[var(--pos-m)] md:object-[var(--pos-d)]" data-testid="hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent md:via-black/30" />
        <div className="absolute inset-0 bg-black" style={{ opacity: (h.overlay ?? 45) / 100 * 0.6 }} />
      </div>
      <div className="relative z-10 -mt-20 md:mt-0 md:absolute md:inset-0 mx-auto w-full max-w-7xl flex flex-col justify-end px-6 md:px-8 pb-12 md:pb-24">
        <p className="eyebrow fade-up">{tx("hero_eyebrow", t("home.eyebrow"))}</p>
        <h1 className="mt-4 font-semibold tracking-tight chrome-text max-w-3xl leading-[1.1] fade-up break-words text-4xl sm:text-5xl lg:text-6xl" style={{ animationDelay: "120ms" }} data-testid="hero-title">
          {tx("hero_title", content.hero_title?.[lang] || t("home.title"))}
        </h1>
        <p className="mt-4 md:mt-5 text-zinc-300 max-w-xl fade-up text-base md:text-lg" style={{ animationDelay: "220ms" }} data-testid="hero-subtitle">{tx("hero_subtitle", content.hero_subtitle?.[lang] || "")}</p>
        <div className="flex flex-wrap gap-3 fade-up mt-7 md:mt-9" style={{ animationDelay: "320ms" }}>
          <Link to={links.hero_cta} className="btn-xd" data-testid="hero-inventory-button">{tx("hero_cta", t("home.cta"))}<ArrowLeft size={18} /></Link>
          {site?.show_cta2 !== false && <Link to={links.hero_cta2} className="btn-ghost" data-testid="hero-sold-button">{tx("hero_cta2", t("nav.sold"))}</Link>}
        </div>
      </div>
      <div className="absolute bottom-0 inset-x-0 xd-line" />
    </section>
  );
};

const Categories = ({ site }) => {
  const { t, lang } = useLang();
  const { categories } = useConfig();
  if (!categories.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-6 md:px-8 pt-20 md:pt-28" data-testid="categories-section">
      <p className="eyebrow">{siteText(site, "categories_title", lang, t("home.categories"))}</p>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5 border border-white/5">
        {categories.map((c, i) => (
          <Link key={c.id} to={`/inventory?category=${c.id}`} data-testid={`category-tile-${c.slug}`}
            className="group relative bg-black p-6 md:p-8 aspect-[4/3] lg:aspect-square flex flex-col justify-between hover:bg-[#0b0b0b] transition-colors min-w-0">
            <span className="text-xs text-zinc-600 ltr-num">0{i + 1}</span>
            <span className="text-lg md:text-xl font-medium group-hover:text-white text-zinc-200 break-words">{lang === "ar" ? c.name_ar : c.name_he}</span>
            <span className="absolute bottom-0 inset-x-0 h-px bg-[var(--xd-red)] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-right" />
          </Link>
        ))}
      </div>
    </section>
  );
};

const Featured = ({ site }) => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/public/vehicles", { params: { featured: true } }).then((r) => setItems(r.data.items)).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-6 md:px-8 pt-20 md:pt-28" data-testid="featured-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="min-w-0">
          <p className="eyebrow">{siteText(site, "featured_eyebrow", lang, t("home.featured_eyebrow"))}</p>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold chrome-text break-words">{siteText(site, "featured_title", lang, t("home.featured"))}</h2>
        </div>
        <Link to="/inventory" className="text-sm text-zinc-400 hover:text-white" data-testid="featured-all-link">{t("home.view_all")}</Link>
      </div>
      <VehicleGrid items={items.slice(0, 6)} />
    </section>
  );
};

const Values = ({ site }) => {
  const { t, lang } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-6 md:px-8 pt-20 md:pt-28" data-testid="values-section">
      <div className="grid md:grid-cols-3 gap-10 border-y border-white/5 py-14">
        {[1, 2, 3].map((n) => (
          <div key={n} className="space-y-3 min-w-0">
            <span className="text-sm text-[var(--xd-red)] ltr-num">0{n}</span>
            <h3 className="text-lg font-medium">{siteText(site, `value${n}_t`, lang, t(`home.value${n}_t`))}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{siteText(site, `value${n}_d`, lang, t(`home.value${n}_d`))}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const Contact = ({ site }) => {
  const { t, lang } = useLang();
  const { whatsapp } = useConfig();
  if (!whatsapp) return null;
  return (
    <section className="mx-auto max-w-7xl px-6 md:px-8 pt-16" data-testid="contact-section">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <h3 className="text-2xl md:text-3xl font-semibold chrome-text max-w-xl break-words">{siteText(site, "contact_title", lang, t("home.contact_title"))}</h3>
        <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="btn-xd" data-testid="home-whatsapp-button">
          <MessageCircle size={18} />{siteText(site, "contact_button", lang, t("vehicle.whatsapp"))}
        </a>
      </div>
    </section>
  );
};

const SECTIONS = { categories: Categories, featured: Featured, values: Values, contact: Contact };

const PreviewBar = () => {
  const { t } = useLang();
  return (
    <div className="fixed bottom-4 inset-x-4 z-50 mx-auto max-w-md bg-amber-400 text-black text-sm font-medium px-4 py-3 flex items-center gap-2 shadow-2xl" data-testid="site-preview-banner">
      <Eye size={16} />{t("site.preview_banner")}
    </div>
  );
};

export default function Home() {
  const site = useSite();
  if (!site) return <div className="h-[85svh] bg-black" />;
  const secs = site.sections || Object.keys(SECTIONS).map((id) => ({ id, visible: true }));
  return (
    <div>
      <HeroView site={site} />
      {secs.filter((s) => s.visible && SECTIONS[s.id]).map((s) => { const C = SECTIONS[s.id]; return <C key={s.id} site={site} />; })}
      {isPreview() && <PreviewBar />}
    </div>
  );
}
